import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

// ── 类型 ────────────────────────────────────────────────────────────────
interface CharParticle {
  id: string
  char: string
  // 目标排版坐标（聚合态）
  tx: number
  ty: number
  // 初始散乱坐标（种子态）
  sx: number
  sy: number
  // 随机噪声偏移（漂浮感）
  noiseX: number
  noiseY: number
  noisePhase: number
  delay: number
}

interface TextSeedMorphProps {
  lines: string[]
  /** 触发聚合的条件: 'hover'=鼠标进入容器 | 'scroll'=页面滚动 | 'auto'=自动(3s后) */
  trigger?: 'hover' | 'scroll' | 'auto'
  /** 字体大小（px），移动端会自动缩减 */
  fontSize?: number
  /** 字体粗细 */
  fontWeight?: string
  /** 聚合后的字色 */
  color?: string
  /** 散乱粒子颜色 */
  seedColor?: string
  className?: string
}

// ── Pretext-style 排版引擎 ────────────────────────────────────────────────
// 用 Canvas measureText 实现精确字符级排版坐标计算
function computeLayout(
  lines: string[],
  fontSize: number,
  fontWeight: string,
  containerWidth: number
): { chars: Array<{ char: string; x: number; y: number }>; totalHeight: number } {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const lineHeight = fontSize * 1.45
  const chars: Array<{ char: string; x: number; y: number }> = []

  lines.forEach((line, lineIdx) => {
    // 计算整行宽度，居中对齐
    ctx.font = `${fontWeight} ${fontSize}px "PingFang SC", "Noto Sans SC", sans-serif`
    const lineWidth = ctx.measureText(line).width
    const startX = (containerWidth - lineWidth) / 2
    let curX = startX
    const curY = lineIdx * lineHeight

    for (const char of line) {
      const w = ctx.measureText(char).width
      chars.push({ char, x: curX + w / 2, y: curY })
      curX += w
    }
  })

  return { chars, totalHeight: lines.length * lineHeight }
}

// ── 主组件 ────────────────────────────────────────────────────────────────
export default function TextSeedMorph({
  lines,
  trigger = 'auto',
  fontSize = 52,
  fontWeight = '900',
  color: _color = '#ffffff',
  seedColor = 'rgba(96,165,250,0.75)',
  className = '',
}: TextSeedMorphProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const animFrameRef = useRef<number>(0)
  const prefersReduced = useReducedMotion()

  const [particles, setParticles] = useState<CharParticle[]>([])
  const [gathered, setGathered] = useState(false)
  const [containerSize, setContainerSize] = useState({ w: 800, h: 200 })
  const [mounted, setMounted] = useState(false)

  // ── 初始化粒子 ─────────────────────────────────────────────────────────
  const initParticles = useCallback(
    (containerW: number) => {
      const responsiveFontSize = containerW < 640 ? fontSize * 0.55 : containerW < 1024 ? fontSize * 0.75 : fontSize
      const { chars, totalHeight } = computeLayout(lines, responsiveFontSize, fontWeight, containerW)

      const newParticles: CharParticle[] = chars.map((c, i) => {
        const angle = Math.random() * Math.PI * 2
        const radius = 150 + Math.random() * (containerW * 0.35)
        return {
          id: `${i}-${c.char}`,
          char: c.char,
          tx: c.x,
          ty: c.y,
          sx: containerW / 2 + Math.cos(angle) * radius,
          sy: totalHeight / 2 + Math.sin(angle) * radius * 0.5,
          noiseX: (Math.random() - 0.5) * 28,
          noiseY: (Math.random() - 0.5) * 18,
          noisePhase: Math.random() * Math.PI * 2,
          delay: i * 0.018 + Math.random() * 0.06,
        }
      })

      setParticles(newParticles)
      setContainerSize({ w: containerW, h: totalHeight + responsiveFontSize * 1.5 })
    },
    [lines, fontSize, fontWeight]
  )

  // ── 监听容器宽度 ───────────────────────────────────────────────────────
  useEffect(() => {
    setMounted(true)
    const el = containerRef.current
    if (!el) return

    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width
      if (w > 0) initParticles(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [initParticles])

  // ── 触发器逻辑 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mounted) return
    if (prefersReduced) { setGathered(true); return }

    if (trigger === 'auto') {
      const t = setTimeout(() => setGathered(true), 2200)
      return () => clearTimeout(t)
    }

    if (trigger === 'scroll') {
      const onScroll = () => {
        if (window.scrollY > 40) { setGathered(true) }
      }
      window.addEventListener('scroll', onScroll, { passive: true })
      return () => window.removeEventListener('scroll', onScroll)
    }
    // hover 模式在 JSX 里处理
  }, [mounted, trigger, prefersReduced])

  // ── 漂浮噪声动画（种子态时持续飘动）──────────────────────────────────
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (gathered) return
    let last = 0
    const loop = (t: number) => {
      if (t - last > 50) { setTick((v) => v + 1); last = t }
      animFrameRef.current = requestAnimationFrame(loop)
    }
    animFrameRef.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [gathered])

  if (!mounted || particles.length === 0) {
    return <div ref={containerRef} className={`relative w-full ${className}`} style={{ minHeight: 120 }} />
  }

  const responsiveFontSize =
    containerSize.w < 640 ? fontSize * 0.55 : containerSize.w < 1024 ? fontSize * 0.75 : fontSize

  return (
    <div
      ref={containerRef}
      className={`relative w-full select-none ${className}`}
      style={{ height: containerSize.h }}
      onMouseEnter={() => trigger === 'hover' && setGathered(true)}
    >
      {/* 种子态粒子 + 聚合态字符 */}
      {particles.map((p) => {
        const phase = (tick * 0.06 + p.noisePhase) % (Math.PI * 2)
        const floatX = gathered ? 0 : Math.sin(phase) * p.noiseX
        const floatY = gathered ? 0 : Math.cos(phase * 0.7) * p.noiseY

        // 聚合态：文字颜色根据位置用渐变映射
        const gradientProgress = particles.length > 1 ? particles.indexOf(p) / (particles.length - 1) : 0
        // 蓝→紫渐变
        const r = Math.round(59 + (139 - 59) * gradientProgress)
        const g = Math.round(130 + (92 - 130) * gradientProgress)
        const b = Math.round(246 + (246 - 246) * gradientProgress)
        const charColor = gathered ? `rgb(${r},${g},${b})` : seedColor

        return (
          <motion.span
            key={p.id}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              fontSize: responsiveFontSize,
              fontWeight,
              fontFamily: '"PingFang SC", "Noto Sans SC", "Microsoft YaHei", sans-serif',
              lineHeight: 1,
              userSelect: 'none',
              pointerEvents: 'none',
              color: charColor,
              // 种子态：半透明、略有模糊；聚合态：清晰
              filter: gathered ? 'blur(0px)' : 'blur(0.6px)',
              transformOrigin: 'center center',
              willChange: 'transform, opacity, color, filter',
            }}
            initial={false}
            animate={{
              x: gathered ? p.tx + floatX : p.sx + floatX,
              y: gathered ? p.ty + floatY : p.sy + floatY,
              opacity: gathered ? 1 : 0.55 + Math.sin(phase) * 0.2,
              scale: gathered ? 1 : 0.72 + Math.sin(phase * 1.3) * 0.12,
              rotate: gathered ? 0 : Math.sin(phase * 0.5 + p.noisePhase) * 8,
              color: charColor,
              filter: gathered ? 'blur(0px)' : 'blur(0.6px)',
            }}
            transition={
              gathered
                ? {
                    type: 'spring',
                    stiffness: 180,
                    damping: 22,
                    mass: 0.8,
                    delay: p.delay,
                  }
                : { duration: 0 }
            }
          >
            {p.char === ' ' ? '\u00A0' : p.char}
          </motion.span>
        )
      })}

      {/* 聚合完成后叠加文字阴影光晕层 */}
      {gathered && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: particles.length * 0.018 + 0.5, duration: 0.8 }}
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(59,130,246,0.08), transparent 70%)',
            filter: 'blur(20px)',
          }}
        />
      )}
    </div>
  )
}
